'use client';

import { useState } from 'react';
import { createPortal } from 'react-dom';

import { classNames } from '@/shared/lib/classNames/classNames';

interface DevicesByCityItem {
  city: string;
  text: string;
  iframe: string;
}

export const DevicesByCity = ({
  images,
  devicesByCity,
  dataVisualizationNote,
}: {
  images: { fullScreen: string; close: string };
  devicesByCity: DevicesByCityItem[];
  dataVisualizationNote: string;
}) => {
  const [activeCityIndex, setActiveCityIndex] = useState<number>(0);

  return (
    <div>
      <div className="flex flex-wrap gap-2 max-sm:flex-nowrap max-sm:overflow-x-auto">
        {devicesByCity.map((item, index) => (
          <div
            className={classNames({
              cls: 'flex h-[36px] cursor-pointer items-center justify-center rounded-[8px] px-4 text-[14px] leading-[20px] max-sm:shrink-0',
              mods: {
                'bg-[#19AA1E]': activeCityIndex === index,
                'bg-[#283136]': activeCityIndex !== index,
              },
            })}
            key={item.city}
            onClick={() => setActiveCityIndex(index)}
          >
            {item.city}
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-[20px] bg-[#283136] p-2 pt-7 max-sm:flex max-sm:flex-col-reverse max-sm:p-1">
        <div className="max-sm:mt-3 max-sm:px-4 max-sm:pb-6">
          <p className="pl-6 text-[30px] leading-[38px] font-medium max-sm:pl-0 max-sm:text-[24px] max-sm:leading-[32px]">
            {devicesByCity[activeCityIndex].city}
          </p>

          <p className="mt-3 pl-6 text-[16px] leading-[24px] max-sm:pl-0">
            {devicesByCity[activeCityIndex].text}
          </p>

          <p className="mt-3 pl-6 text-[14px] leading-[20px] opacity-[50%] max-sm:pl-0">
            {dataVisualizationNote}
          </p>
        </div>

        <Iframe
          src={devicesByCity[activeCityIndex].iframe}
          key={devicesByCity[activeCityIndex].iframe}
          images={images}
        />
      </div>
    </div>
  );
};

const Iframe = ({
  src,
  images,
}: {
  src: string;
  images: { fullScreen: string; close: string };
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenFullScreen = () => {
    setIsOpen(true);
  };

  const handleCloseFullScreen = () => {
    setIsOpen(false);
  };

  return (
    <>
      <div className="relative mt-7 h-[386px] w-full overflow-hidden rounded-[12px] max-sm:mt-0 max-sm:h-[396px]">
        <iframe src={src} className="h-full w-full" />

        <img
          src={images.fullScreen}
          alt="fullScreenIcon"
          className="absolute top-1/2 left-[16px] w-[40px] -translate-y-1/2 cursor-pointer max-lg:top-[unset] max-lg:bottom-[16px] max-lg:left-[16px] max-lg:translate-y-0"
          onClick={handleOpenFullScreen}
        />
      </div>

      {isOpen &&
        createPortal(
          <div className="fixed inset-0 z-[1001] h-full w-full bg-black/70">
            <iframe src={src} className="relative h-full w-full"></iframe>

            <img
              src={images.close}
              alt="closeIcon"
              className="absolute top-1/2 left-[16px] w-[40px] -translate-y-1/2 cursor-pointer max-lg:top-[unset] max-lg:bottom-[16px] max-lg:left-[16px] max-lg:translate-y-0"
              onClick={handleCloseFullScreen}
            />
          </div>,
          document.body,
        )}
    </>
  );
};
